import React, { useState, useRef, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, LayersControl, } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import Sidebar from "./Sidebar";
import Modal from "./Modal";
import Dashboard from "./Dashboard";
import DnoData from "./DnoData";
import { Colors } from "chart.js";

function MapView() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [map, setMap] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [aoi, setAoi] = useState("Bournemouth, UK");
  const [roof_material, setRoofMaterial] = useState("");
  const [minimum_yearly_kwh, setMinimumYearlyKwh] = useState("");
  const [solar_panel_number_min, setSolarPanelNumberMin] = useState("");
  const [dno_rating, setDnoRating] = useState("");
  const [ownershipCategory, setOwnershipCategory] = useState("");
  const [ZoomLevel, setZoomLevel] = useState(12);

  let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
  });

  const handleFormSubmit = (data) => {
    setAoi(data.areaOfInterest);
    setRoofMaterial(data.roofMaterial);
    setMinimumYearlyKwh(data.minimumYearlyKwh);
    setSolarPanelNumberMin(data.solarPanelNumberMin);
    setDnoRating(data.dnoRating);
    setOwnershipCategory(data.ownershipCategory);
  };

  const filterData = () => {
    let data = [...originalData];

    if (roof_material !== "") {
      data = data.filter((point) => point.roof_material === roof_material);
    }

    if (minimum_yearly_kwh !== "") {
      data = data.filter(
        (point) => point.yearly_energy_dc_kwh >= minimum_yearly_kwh
      );
    }

    if (solar_panel_number_min !== "") {
      data = data.filter(
        (point) => point.panels_count >= solar_panel_number_min
      );
    }

    if (aoi === "Bournemouth, UK") {
      if (dno_rating !== "") {
        data = data.filter(
          (point) =>
            point?.dno_info?.substation_info?.color_rating === dno_rating
        );
      }

      if (ownershipCategory !== "") {
        data = data.filter((point) =>
          point?.land_reg_info?.some(
            (info) => info.ownership_category === ownershipCategory
          )
        );
      }
    }

    if (aoi === "Coventry, UK") {
      if (dno_rating !== "") {
        data = data.filter(
          (point) =>
            point?.dno_info?.headroom_info?.[0]?.Generation_Headroom_RAG ===
            dno_rating
        );
      }

      if (ownershipCategory !== "") {
        data = data.filter((point) =>
          point?.land_reg_info?.some(
            (info) => info.ownership_category === ownershipCategory
          )
        );
      }
    }

    if (aoi === "Poole, UK") {
      if (dno_rating !== "") {
        data = data.filter(
          (point) =>
            point?.dno_info?.substation_info?.color_rating === dno_rating
        );
      }

      if (ownershipCategory !== "") {
        data = data.filter((point) =>
          point?.land_reg_info?.some(
            (info) => info.ownership_category === ownershipCategory
          )
        );
      }
    }

    if (aoi === "Southampton, UK") {
      if (dno_rating !== "") {
        data = data.filter(
          (point) =>
            point?.dno_info?.substation_info?.color_rating === dno_rating
        );
      }

      if (ownershipCategory !== "") {
        data = data.filter((point) =>
          point?.land_reg_info?.some(
            (info) => info.ownership_category === ownershipCategory
          )
        );
      }
    }

    if (aoi === "Portsmouth, UK") {
      if (dno_rating !== "") {
        data = data.filter(
          (point) =>
            point?.dno_info?.substation_info?.color_rating === dno_rating
        );
      }

      if (ownershipCategory !== "") {
        data = data.filter((point) =>
          point?.land_reg_info?.some(
            (info) => info.ownership_category === ownershipCategory
          )
        );
      }
    }

    if (aoi === "Aylesbury, UK") {
      if (dno_rating !== "") {
        data = data.filter(
          (point) =>
            point?.dno_info?.substation_info?.color_rating === dno_rating
        );
      }

      if (ownershipCategory !== "") {
        data = data.filter((point) =>
          point?.land_reg_info?.some(
            (info) => info.ownership_category === ownershipCategory
          )
        );
      }
    }

    setFilteredData(data);
  };

  L.Marker.prototype.options.icon = DefaultIcon;
  const mapRef = useRef(null);
  const [center, setCenter] = useState([50.7192, -1.8808]);

  useEffect(() => {
    fetchProfile();
    async function fetchProfile() {
      const response = await fetch("/api/profile", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok) {
        return;
      }
      if (data.data.email === "andy@ecosite.uk") {
        setZoomLevel(18);
      }
    }
  }, []);

  useEffect(() => {
    if (map) {
      map.setView(center);
      map.invalidateSize();
      map.setMaxZoom(ZoomLevel);
    }
  }, [isSidebarOpen, map, filteredData, center, ZoomLevel]);

  useEffect(() => {
    const url = `/api/sites?aoi=${aoi}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        setOriginalData(data);
        setFilteredData(data);
        if (data === null) {
          setFilteredData([]);
          setCenter([50.7192, -1.8808]);
          return;
        }
        setCenter([data[0].lat, data[0].lng]);
      });
    setRoofMaterial("");
    setMinimumYearlyKwh("");
    setSolarPanelNumberMin("");
    setDnoRating("");
    setOwnershipCategory("");
  }, [aoi]);

  useEffect(() => {
    filterData();
  }, [
    roof_material,
    minimum_yearly_kwh,
    solar_panel_number_min,
    dno_rating,
    ownershipCategory,
  ]);

  return (
    <div className="flex h-screen relative">
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <Dashboard data={filteredData} />
      </Modal>
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        handleFormSubmit={handleFormSubmit}
      />
      <div className="w-full z-10">
        <MapContainer
          center={center}
          zoom={10}
          className="h-screen"
          touchZoom={true}
          zoomControl={true}
          ref={setMap}
          maxZoom={ZoomLevel}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
	  <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="OpenStreetMap">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Google Satellite">
              <TileLayer
                url={`https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}&key=`}
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          <MarkerClusterGroup>
            {filteredData.map((point, index) => (
              <Marker
                key={index}
                position={[point.lat, point.lng]}
                title={point.building_id}
              >
                <Popup>
                  <div className="max-h-72 overflow-y-auto">
                    <h2 className="text-black">AOI Name : {point.aoi_name}</h2>
                    <p className="text-black">
                      Building ID : {point.building_id}
                    </p>
                    <p className="text-black">
                      Roof material : {point.roof_material}
                    </p>
                    <p className="text-black">Roof Area : {point.roof_area}</p>
                    <p className="text-black">
                      Panel Count: {point.panels_count}
                    </p>
                    <p className="text-black">
                      Installation Cost : {point.installation_cost}
                    </p>
                    <p className="text-black">
                      Yearly Energy DC Kwh : {point.yearly_energy_dc_kwh}
                    </p>
                    <p className="text-black">
                      Installation Size KW Available Area :{" "}
                      {point.installation_size_kw_available_area}
                    </p>
                    {<DnoData AOI={point.aoi_name} point={point} />}
                    <button
                      type="submit"
                      className="w-full bg-[#2d9376] text-white py-2 px-4 rounded-lg hover:bg-[#2d9376] mt-8"
                      onClick={() => setModalOpen(true)}
                    >
                      Graphical overview
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
}

export default MapView;
